import {
  Document,
  Paragraph,
  Table,
  TableRow,
  TableCell,
  Packer,
  convertInchesToTwip,
  HeadingLevel,
  TextRun,
  UnderlineType,
  WidthType,
  ShadingType,
  AlignmentType,
  LevelFormat,
} from 'docx';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import {rehypeBenchlingTags} from 'llm/markdown/LLMMarkdownRehype';
import {remarkBenchlingTags} from 'llm/markdown/LLMMarkdownRemark';
import {createBenchlingTagConfigs, createSanitizeSchema} from 'llm/markdown/LLMMarkdownSection';
import {remarkGfmTables} from 'ui-library/remarkGfmTables';

// Types for processing HTML to docx
interface TextFormatting {
  bold?: boolean;
  italics?: boolean;
  underline?: {type: (typeof UnderlineType)[keyof typeof UnderlineType]};
  color?: string;
  font?: string;
  size?: number;
  shading?: {type: (typeof ShadingType)[keyof typeof ShadingType]; fill: string};
  strike?: boolean;
  superScript?: boolean;
  subScript?: boolean;
}

interface ProcessingContext {
  formatting?: TextFormatting;
  listLevel?: number;
  listType?: 'bullet' | 'number';
  listInstance?: number;
  insideTable?: boolean;
  listInstanceCounter?: {current: number};
  insideBlockquote?: boolean;
}

export async function convertMarkdownToWord(markdownContent: string): Promise<Blob> {
  const tagConfigs = createBenchlingTagConfigs({});
  const sanitizeSchema = createSanitizeSchema(tagConfigs);

  const markdownElement = React.createElement(
    ReactMarkdown as React.ElementType,
    {
      allowedElements: sanitizeSchema.tagNames,
      remarkPlugins: [[remarkBenchlingTags, tagConfigs], remarkGfmTables],
      rehypePlugins: [
        [rehypeBenchlingTags, tagConfigs],
        [rehypeSanitize, sanitizeSchema],
      ],
    },
    markdownContent
  );

  const htmlContent = ReactDOMServer.renderToStaticMarkup(markdownElement);

  const children = await convertHtmlToDocx(htmlContent);

  const doc = new Document({
    numbering: {
      config: [
        {
          reference: 'default-numbering',
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 1,
              format: LevelFormat.LOWER_LETTER,
              text: '%2.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(1), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 2,
              format: LevelFormat.LOWER_ROMAN,
              text: '%3.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(1.5), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 3,
              format: LevelFormat.DECIMAL,
              text: '%4.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(2), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 4,
              format: LevelFormat.LOWER_LETTER,
              text: '%5.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(2.5), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 5,
              format: LevelFormat.LOWER_ROMAN,
              text: '%6.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(3), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 6,
              format: LevelFormat.DECIMAL,
              text: '%7.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(3.5), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 7,
              format: LevelFormat.LOWER_LETTER,
              text: '%8.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(4), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
            {
              level: 8,
              format: LevelFormat.LOWER_ROMAN,
              text: '%9.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: convertInchesToTwip(4.5), hanging: convertInchesToTwip(0.25)},
                },
              },
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
          },
        },
        children,
      },
    ],
    styles: {
      default: {
        heading1: {
          run: {
            size: 32,
            bold: true,
            color: '000000',
          },
          paragraph: {
            spacing: {
              before: 240,
              after: 120,
            },
          },
        },
        heading2: {
          run: {
            size: 28,
            bold: true,
            color: '000000',
          },
          paragraph: {
            spacing: {
              before: 240,
              after: 120,
            },
          },
        },
        heading3: {
          run: {
            size: 24,
            bold: true,
            color: '000000',
          },
          paragraph: {
            spacing: {
              before: 240,
              after: 120,
            },
          },
        },
        document: {
          run: {
            size: 22,
            font: 'Calibri',
          },
          paragraph: {
            spacing: {
              after: 120,
              line: 276,
            },
          },
        },
      },
    },
  });

  // Generate the Word document blob
  return await Packer.toBlob(doc);
}

/** @package exported for testing */
export async function convertHtmlToDocx(htmlContent: string): Promise<Array<Paragraph | Table>> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const body = doc.body;

  // Counter for numbered list instances
  const listInstanceCounter = {current: 0};
  return await processHtmlElements(Array.from(body.children), listInstanceCounter);
}

// Helper functions
const BLOCK_ELEMENTS = new Set([
  'P',
  'DIV',
  'BLOCKQUOTE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'UL',
  'OL',
  'LI',
  'TABLE',
  'PRE',
  'HR',
]);

const INLINE_ELEMENTS = new Set([
  'SPAN',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'CODE',
  'A',
  'S',
  'DEL',
  'Q',
  'IDCHIP',
  'CITATIONCHIP',
  'WEBCITATIONCHIP',
  'SUB',
  'SUP',
]);

function isBlockElement(element: Element): boolean {
  return BLOCK_ELEMENTS.has(element.tagName.toUpperCase());
}

function isInlineElement(element: Element): boolean {
  return INLINE_ELEMENTS.has(element.tagName.toUpperCase());
}

function getTextFormatting(element: Element, baseFormatting: TextFormatting = {}): TextFormatting {
  const tagName = element.tagName.toLowerCase();
  const newFormatting = {...baseFormatting};

  switch (tagName) {
    case 'strong':
    case 'b':
      newFormatting.bold = true;
      break;
    case 'em':
    case 'i':
      newFormatting.italics = true;
      break;
    case 'u':
      newFormatting.underline = {type: UnderlineType.SINGLE};
      break;
    case 'code':
      newFormatting.font = 'Courier New';
      newFormatting.shading = {type: ShadingType.CLEAR, fill: 'E6E6E6'};
      break;
    case 'a':
      newFormatting.color = '0563C1';
      newFormatting.underline = {type: UnderlineType.SINGLE};
      break;
    case 's':
    case 'del':
      newFormatting.strike = true;
      break;
    case 'sup':
      newFormatting.superScript = true;
      break;
    case 'sub':
      newFormatting.subScript = true;
      break;
    case 'idchip':
      newFormatting.color = '0563C1';
      newFormatting.bold = true;
      break;
    case 'citationchip':
    case 'webcitationchip':
      newFormatting.color = '0563C1';
      newFormatting.superScript = true;
      break;
    default:
      break;
  }

  return newFormatting;
}

async function processHtmlElements(
  elements: Array<Element>,
  listInstanceCounter: {current: number} = {current: 0}
): Promise<Array<Paragraph | Table>> {
  const results: Array<Paragraph | Table> = [];
  let currentTextRuns: Array<TextRun> = [];

  // Helper to flush accumulated text runs into a paragraph
  function flushParagraph(): void {
    if (currentTextRuns.length > 0) {
      results.push(
        new Paragraph({
          children: currentTextRuns,
        })
      );
      currentTextRuns = [];
    }
  }

  for (const element of elements) {
    const tagName = element.tagName.toUpperCase();

    // Handle BR - force new paragraph
    if (tagName === 'BR') {
      flushParagraph();
      continue;
    }

    // Handle block elements
    if (isBlockElement(element)) {
      flushParagraph();
      const blockResults = await processHtmlElement(element, {listInstanceCounter}, listInstanceCounter);
      results.push(...blockResults);
      continue;
    }

    // Handle inline elements
    if (isInlineElement(element) || element.nodeType === Node.TEXT_NODE) {
      const textRuns = createTextRunsFromHtml(element, {});
      currentTextRuns.push(...textRuns);
      continue;
    }

    // Default: try to process as block
    flushParagraph();
    const defaultResults = await processHtmlElement(element, {listInstanceCounter}, listInstanceCounter);
    results.push(...defaultResults);
  }

  // Flush any remaining text runs
  flushParagraph();

  return results;
}

async function processHtmlElement(
  element: Element,
  context: ProcessingContext,
  listInstanceCounter: {current: number} = {current: 0}
): Promise<Array<Paragraph | Table>> {
  const tagName = element.tagName.toUpperCase();

  // Handle headings
  if (tagName.match(/^H[1-6]$/)) {
    const level = parseInt(tagName[1], 10);
    return [createHeadingFromHtml(element, level, context)];
  }

  // Handle tables
  if (tagName === 'TABLE') {
    const table = createTableFromHtml(element as HTMLTableElement, context);
    return [table];
  }

  // Handle lists
  if (tagName === 'UL' || tagName === 'OL') {
    const counter = context.listInstanceCounter || listInstanceCounter;
    counter.current += 1;
    const contextWithInstance = {...context, listInstance: counter.current, listInstanceCounter: counter};
    return createListFromHtml(element, tagName === 'UL' ? 'bullet' : 'number', contextWithInstance);
  }

  // Handle paragraphs and divs
  if (tagName === 'P' || tagName === 'DIV') {
    return [createParagraphFromBlock(element, context)];
  }

  // Handle blockquotes
  if (tagName === 'BLOCKQUOTE') {
    const blockquoteContext = {...context, insideBlockquote: true};
    const results: Array<Paragraph | Table> = [];

    for (const child of Array.from(element.children)) {
      const childResults = await processHtmlElement(child, blockquoteContext, listInstanceCounter);
      results.push(...childResults);
    }

    return results;
  }

  // Handle pre/code blocks
  if (tagName === 'PRE') {
    const text = element.textContent || '';
    return [
      new Paragraph({
        children: [
          new TextRun({
            text,
            font: 'Courier New',
            size: 20,
          }),
        ],
      }),
    ];
  }

  // Handle horizontal rules
  if (tagName === 'HR') {
    return [
      new Paragraph({
        children: [new TextRun('')],
        border: {
          bottom: {
            color: '000000',
            space: 1,
            style: 'single',
            size: 6,
          },
        },
      }),
    ];
  }

  // Default: create a paragraph from the element's content
  if (element.children.length > 0) {
    const children = Array.from(element.children);
    return await processHtmlElements(children, listInstanceCounter);
  }

  // If no children, create a simple paragraph with text content
  const text = element.textContent || '';
  if (text.trim()) {
    return [
      new Paragraph({
        children: [new TextRun(text)],
      }),
    ];
  }

  return [];
}

/**
 * Create a heading from HTML element
 */
function createHeadingFromHtml(element: Element, level: number, context: ProcessingContext): Paragraph {
  const headingLevel = [
    HeadingLevel.HEADING_1,
    HeadingLevel.HEADING_2,
    HeadingLevel.HEADING_3,
    HeadingLevel.HEADING_4,
    HeadingLevel.HEADING_5,
    HeadingLevel.HEADING_6,
  ][level - 1];

  const children = createTextRunsFromHtml(element, context);

  return new Paragraph({
    heading: headingLevel,
    children,
  });
}

/**
 * Create a paragraph from a block element
 */
function createParagraphFromBlock(element: Element, context: ProcessingContext): Paragraph {
  const children = createTextRunsFromHtml(element, context);

  return new Paragraph({
    children: children.length > 0 ? children : [new TextRun('')],
    indent: context.insideBlockquote ? {left: convertInchesToTwip(0.5)} : undefined,
  });
}

/**
 * Create list paragraphs from UL/OL element
 */
function createListFromHtml(
  element: Element,
  listType: 'bullet' | 'number',
  context: ProcessingContext
): Array<Paragraph> {
  const results: Array<Paragraph> = [];
  const currentLevel = context.listLevel || 0;
  const newContext = {...context, listType, listLevel: currentLevel};

  for (const child of Array.from(element.children)) {
    if (child.tagName.toUpperCase() === 'LI') {
      // Separate direct text content from nested lists
      const directContent: Array<Node> = [];
      const nestedLists: Array<Element> = [];

      for (const node of Array.from(child.childNodes)) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const elem = node as Element;
          const tagName = elem.tagName.toUpperCase();
          if (tagName === 'UL' || tagName === 'OL') {
            nestedLists.push(elem);
          } else {
            directContent.push(node);
          }
        } else {
          directContent.push(node);
        }
      }

      // Create a temporary element to extract text runs from direct content only
      const tempElement = document.createElement('span');
      for (const node of directContent) {
        tempElement.appendChild(node.cloneNode(true));
      }
      const textRuns = createTextRunsFromHtml(tempElement, newContext);

      // Add the main list item paragraph
      results.push(
        new Paragraph({
          children: textRuns.length > 0 ? textRuns : [new TextRun('')],
          bullet:
            listType === 'bullet'
              ? {
                  level: Math.min(currentLevel, 9),
                }
              : undefined,
          numbering:
            listType === 'number'
              ? {
                  reference: 'default-numbering',
                  level: Math.min(currentLevel, 9),
                  instance: context.listInstance || 0,
                }
              : undefined,
        })
      );

      // Process any nested lists
      for (const nestedList of nestedLists) {
        const nestedListType = nestedList.tagName.toUpperCase() === 'UL' ? 'bullet' : 'number';
        let nestedContext = {...newContext, listLevel: currentLevel + 1};

        // For nested numbered lists, create a new instance
        if (context.listInstanceCounter) {
          context.listInstanceCounter.current += 1;
          nestedContext = {...nestedContext, listInstance: context.listInstanceCounter.current};
        }

        const nestedResults = createListFromHtml(nestedList, nestedListType, nestedContext);
        results.push(...nestedResults);
      }
    }
  }

  return results;
}

/**
 * Create a table from HTML table element
 */
function createTableFromHtml(element: HTMLTableElement, context: ProcessingContext): Table {
  const rows: Array<TableRow> = [];

  // Process thead
  const thead = element.querySelector('thead');
  if (thead) {
    for (const tr of Array.from(thead.querySelectorAll('tr'))) {
      rows.push(createTableRowFromHtml(tr, context, true));
    }
  }

  // Process tbody
  const tbody = element.querySelector('tbody') || element;
  for (const tr of Array.from(tbody.querySelectorAll('tr'))) {
    // Skip if already processed in thead
    if (thead && thead.contains(tr)) {
      continue;
    }
    rows.push(createTableRowFromHtml(tr, context, false));
  }

  return new Table({
    rows,
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
  });
}

/**
 * Create a table row from HTML tr element
 */
function createTableRowFromHtml(tr: Element, context: ProcessingContext, isHeader: boolean): TableRow {
  const cells: Array<TableCell> = [];

  for (const cell of Array.from(tr.children)) {
    if (cell.tagName.toUpperCase() === 'TD' || cell.tagName.toUpperCase() === 'TH') {
      const textRuns = createTextRunsFromHtml(cell, context);

      cells.push(
        new TableCell({
          children: [
            new Paragraph({
              children: textRuns.length > 0 ? textRuns : [new TextRun('')],
            }),
          ],
          shading: isHeader
            ? {
                fill: 'E6E6E6',
                color: 'auto',
              }
            : undefined,
        })
      );
    }
  }

  return new TableRow({
    children: cells,
  });
}

/**
 * Create TextRuns from HTML element with inline formatting
 */
function createTextRunsFromHtml(element: Element, context: ProcessingContext = {}): Array<TextRun> {
  const runs: Array<TextRun> = [];
  const baseFormatting = context.formatting || {};

  function processNode(node: Node, formatting: TextFormatting = baseFormatting): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text) {
        runs.push(new TextRun({text, ...formatting}));
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const elem = node as Element;
      const tagName = elem.tagName.toLowerCase();

      // Get new formatting based on this element
      const newFormatting = getTextFormatting(elem, formatting);

      // Special handling for certain elements
      if (tagName === 'q') {
        // Add quotes around the content
        const quoteText = `"${elem.textContent || ''}"`;
        runs.push(new TextRun({text: quoteText, ...formatting}));
        return; // Don't process children since we handled the content
      }

      if (tagName === 'br') {
        // Add a line break
        runs.push(new TextRun({text: '', break: 1}));
        return;
      }

      // Process child nodes recursively
      for (const child of Array.from(elem.childNodes)) {
        processNode(child, newFormatting);
      }
    }
  }

  // Process all child nodes
  for (const child of Array.from(element.childNodes)) {
    processNode(child);
  }

  return runs.length > 0 ? runs : [new TextRun('')];
}
